import { ProjectAuthType } from "@prisma/client";
import MaintenancePageComponent from "components/MaintenanceComponent";
import { PublishedPage } from "components/PublishedPageSSR";
import PublishedProvider from "components/PublishedProvider";
import { getCSPHeader } from "get-csp-header";
import { RecaptchaProvider } from "lib/hooks/RecaptchaProvider";
import { setProjectSession } from "lib/project-sessions/project-session-storage";
import type { InterfacesTheme } from "lib/theme/ThemeProvider";
import { GetServerSideProps } from "next";
import { getMaintenanceMode } from "@/config/edge";
import { prisma } from "server/data/prisma";
import { withProjectSessionSSR } from "server/hooks/withProjectSession";
import { PageContentInclude } from "server/models/blocks";
import { getPageWithContent, toPageWithContent } from "server/models/pages";
import { getProjectFromSlugOrCustomDomain } from "server/models/projects";
import { getThemeByProjectId } from "server/models/theme";
import { getFrameAncestorsFromProjectConfig } from "server/models/util/projectConfig";
import { PagePublicOutputSchema } from "server/schemas/pages";
import {
  projectForPublishedPageSchema,
  ProjectWithExtra,
} from "server/schemas/projects";
import { getFeatureFlags } from "server/services/split/split";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { zrpc, type GetFeatureFlagsOutput } from "@/lib/zrpc";
import { V0ListPagesResponse } from "@/server/api/routes/pages/v0_list_pages";
import { withErrorCatchingGetServerSideProps } from "@/server/hooks/withErrorCatching";

const QueryParametersSchema = z.object({
  projectSlug: z.string(),
  pageId: z.string().cuid(),
  noBackground: z.union([z.literal("true"), z.literal("false")]).optional(),
});

type ProjectForPublishedPage = z.infer<typeof projectForPublishedPageSchema>;

type Page = z.infer<typeof PagePublicOutputSchema>;

type RenderMaintenanceModeProps = {
  maintenanceMode: true;
};

type RenderAuthFormProps = {
  maintenanceMode: false;
  authorized: false;
};

export type RenderPageProps = {
  maintenanceMode: false;
  authorized: true;
  sessionId: string;
  project: ProjectForPublishedPage;
  page: Page;
  allPages: Page[];
  splitCheck: GetFeatureFlagsOutput;
  noBackground: boolean;
  theme: InterfacesTheme;
  builderFlags: GetFeatureFlagsOutput;
};

type Props = RenderMaintenanceModeProps | RenderAuthFormProps | RenderPageProps;

export default function EmbeddedPublishedPage(props: Props) {
  const queryClient = useQueryClient();

  if (props.maintenanceMode) {
    return <MaintenancePageComponent />;
  }

  if (props.authorized === false) {
    // Auth'd pages must not be embedded, so we intentionally leave the CSP in
    // its default state which prevents browsers from rendering this page in an
    // iframe. Therefore, users should never see this page at all, so we don't
    // need to bother with styling.
    return (
      <section>
        <h2>Unauthorized</h2>
      </section>
    );
  }

  const {
    project,
    page,
    allPages,
    splitCheck,
    noBackground,
    sessionId,
    theme,
    builderFlags,
  } = props;

  setProjectSession({ sessionId, pageId: page.id });

  const getFeatureFlagsQueryOptions = zrpc.interfaces.queryOptions(
    "get",
    "/api/interfaces/v0/internal/feature-flags",
    { params: { query: { project_id: project.id } } }
  );
  const cachedFeatureFlags = queryClient.getQueryData(
    getFeatureFlagsQueryOptions.queryKey
  );
  if (!cachedFeatureFlags) {
    queryClient.setQueryData(getFeatureFlagsQueryOptions.queryKey, splitCheck);
  }

  // TODO INTRFCS-2342
  // Hydrate the react-query client cache.
  //
  // This is a bit of a hack. We're using setData to hydrate instead of using
  // the [established trpc helpers](https://trpc.io/docs/client/nextjs/server-side-helpers#nextjs-example)
  // to hydrate the cache. I tried them a long time ago in a POC, and cannot
  // remember why they didn't work. I will be revisiting this to try them
  // again, and document why they fail if they do. In the meantime, this
  // works.

  const getProjectQueryOptions = zrpc.interfaces.queryOptions(
    "get",
    "/api/interfaces/v0/interfaces/{interface_id}",
    { params: { path: { interface_id: project.id } } }
  );
  const cachedProject = queryClient.getQueryData<ProjectWithExtra>(
    getProjectQueryOptions.queryKey
  );

  if (!cachedProject) {
    queryClient.setQueryData(getProjectQueryOptions.queryKey, project);
  }

  const getPageQueryOptions = zrpc.interfaces.queryOptions(
    "get",
    "/api/interfaces/v0/interfaces/{interface_id}/pages/{page_id}",
    { params: { path: { interface_id: project.id, page_id: page.id } } }
  );
  const cachedPage = queryClient.getQueryData(getPageQueryOptions.queryKey);

  if (page && !cachedPage) {
    queryClient.setQueryData(getPageQueryOptions.queryKey, page);
  }

  const listPagesQueryOptions = zrpc.interfaces.queryOptions(
    "get",
    "/api/interfaces/v0/interfaces/{interface_id}/pages",
    { params: { path: { interface_id: project.id } } }
  );

  const listPagesData = queryClient.getQueryData<V0ListPagesResponse>(
    listPagesQueryOptions.queryKey
  );

  if (allPages && !listPagesData) {
    queryClient.setQueryData<V0ListPagesResponse>(
      listPagesQueryOptions.queryKey,
      { results: allPages }
    );
  }

  return (
    <PublishedProvider
      project={project}
      interfacesTheme={theme ?? undefined}
      builderFlags={builderFlags}
    >
      <RecaptchaProvider>
        <PublishedPage
          isEmbed={true}
          project={project}
          page={page}
          noBackground={noBackground}
        />
      </RecaptchaProvider>
    </PublishedProvider>
  );
}

export const getServerSideProps: GetServerSideProps<Props> =
  withErrorCatchingGetServerSideProps(
    withProjectSessionSSR<Props>(async ({ query, req, res }) => {
      if (await getMaintenanceMode()) {
        // Set frame-ancestors Content-Security-Policy header to '*' since we
        // can't fetch the project and we're just rendering the maintenance page.
        // Slack thread for context and approval:
        // https://zapier.slack.com/archives/C048W40M39S/p1712174761743049
        res.setHeader("Content-Security-Policy", getCSPHeader("*"));

        return {
          props: {
            maintenanceMode: true,
          },
        };
      }

      const parsedQueryResult = QueryParametersSchema.safeParse(query);
      if (!parsedQueryResult.success) {
        return { notFound: true };
      }
      const params = parsedQueryResult.data;
      const noBackground = params.noBackground === "true";

      const project = await getProjectFromSlugOrCustomDomain(
        params.projectSlug
      );
      if (!project) {
        return { notFound: true };
      }

      // Don't allow embedding for projects with passwords
      // -----------------------------------------------------------------------------
      if (project.authType !== ProjectAuthType.None) {
        res.statusCode = 403;
        return {
          props: {
            maintenanceMode: false,
            authorized: false,
          },
        };
      }

      // Set frame-ancestors Content-Security-Policy header
      // -----------------------------------------------------------------------------
      const frameAncestors = getFrameAncestorsFromProjectConfig(project);
      res.setHeader("Content-Security-Policy", getCSPHeader(frameAncestors));

      const [page, allPages] = await Promise.all([
        getPageWithContent({ id: params.pageId, projectId: project.id }),
        prisma.page
          .findMany({
            where: { projectId: project.id },
            include: PageContentInclude,
          })
          .then((pages) => {
            return pages.map(toPageWithContent);
          }),
      ]);
      if (!page) {
        return {
          notFound: true,
        };
      }

      const splitCheck = await getFeatureFlags({
        zapierUser: undefined,
        project,
      });

      const builderFlags = await getFeatureFlags({
        zapierUser: project.creator,
        project,
      });

      // If the page contains a chatbot block, enable the chatbot for this session
      const enableChatbot = page?.content.blocks.some(
        (blockOnPage) => blockOnPage.type === "chatbot-block"
      );

      req.projectSession.data = {
        ...req.projectSession.data,
        enableChatbot,
        projectId: project.id,
      };
      await req.projectSession.save();

      return {
        props: {
          maintenanceMode: false,
          authorized: true,
          sessionId: req.projectSession.id,
          project: projectForPublishedPageSchema.parse(project),
          page: PagePublicOutputSchema.parse(page),
          allPages: PagePublicOutputSchema.array().parse(allPages),
          noBackground,
          verifyBrowser: false,
          splitCheck,
          builderFlags,
          theme: await getThemeByProjectId(project.id),
        },
      };
    })
  );
